/**
 * Shared reporting-date generation logic.
 *
 * Extracted from ScheduleThermometer / BudgetThermometer to eliminate
 * duplication.  The reliability engine, schedule, and finance modules all
 * need to compute concrete reporting dates from a cadence definition.
 *
 * IMPORTANT: This module is pure date-math — no React, no DOM, no UI.
 *
 * @module reportingDates
 */

// ── Types ──────────────────────────────────────────────────────

export type ReportingCadence = "daily" | "weekly" | "monthly" | "quarterly";

export type ReportingSchedule = {
  cadence: ReportingCadence;
  /** Repeat every N cadence units (default 1). */
  interval?: number;
  /** Day-of-week selection for weekly cadence (length-7, index 0 = Sun). */
  by_weekday?: number[] | boolean[];
  /** Monthly mode: specific day, first weekday, or last weekday of month. */
  monthly_mode?: "day" | "first" | "last";
  /** Day of month (1-31) when monthly_mode is "day". */
  monthly_day_of_month?: number;
  /** Weekday (0=Sun … 6=Sat) for "first"/"last" monthly modes. */
  monthly_weekday?: number;
  /** HH:MM local time (reserved for future use). */
  due_time_local?: string;
  /** IANA timezone (reserved for future use). */
  timezone?: string;
  /** ISO date (YYYY-MM-DD) after which no more dates are generated. */
  until_date?: string;
};

// ── Internal date helpers ──────────────────────────────────────

/** Parse "YYYY-MM-DD" → local-midnight ms (avoids UTC-shift pitfalls). */
function parseIsoToLocalMs(iso: string): number {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d).getTime();
}

/** ms → "YYYY-MM-DD" in local timezone. */
function isoFromMs(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Add calendar days to an ms timestamp. */
function addDaysMs(ms: number, days: number): number {
  return ms + days * 86_400_000;
}

/** Add N months, clamping day-of-month to the target month's max. */
function addMonths(ms: number, months: number): number {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const target = new Date(y, m + months, 1);
  const maxDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, maxDay));
  return target.getTime();
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function setDayOfMonth(year: number, month0: number, day: number): number {
  const maxDay = new Date(year, month0 + 1, 0).getDate();
  const d = clamp(Math.floor(Number(day) || 1), 1, maxDay);
  return new Date(year, month0, d).getTime();
}

function firstWeekdayOfMonth(year: number, month0: number, weekday: number): number {
  const wd = clamp(Math.floor(Number(weekday) || 0), 0, 6);
  const first = new Date(year, month0, 1);
  const delta = (wd - first.getDay() + 7) % 7;
  first.setDate(1 + delta);
  return first.getTime();
}

function lastWeekdayOfMonth(year: number, month0: number, weekday: number): number {
  const wd = clamp(Math.floor(Number(weekday) || 0), 0, 6);
  const last = new Date(year, month0 + 1, 0);
  const delta = (last.getDay() - wd + 7) % 7;
  last.setDate(last.getDate() - delta);
  return last.getTime();
}

// ── Public API ─────────────────────────────────────────────────

/**
 * Generate an array of concrete ISO reporting dates within [start, end].
 *
 * Supports daily, weekly (with day-of-week selection), monthly (by day,
 * first weekday, or last weekday), and quarterly cadences.
 *
 * This is the **single source of truth** for date generation — Schedule,
 * Finance, and Data Reliability all call this function.
 */
export function buildReportingDates(
  start: string,
  end: string,
  cadence: ReportingCadence,
  interval: number,
  weekdaysOpt?: boolean[],
  monthlyModeOpt?: "day" | "first" | "last",
  monthlyDayOpt?: number,
  monthlyWeekdayOpt?: number,
): string[] {
  const s = parseIsoToLocalMs(start);
  const e = parseIsoToLocalMs(end);
  const step = Math.max(1, Math.floor(Number(interval || 1)));

  if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return [];

  const out: string[] = [];

  const push = (ms: number) => out.push(isoFromMs(ms));

  const applyMonthlyRule = (ms: number) => {
    const d = new Date(ms);
    const y = d.getFullYear();
    const m0 = d.getMonth();
    const mode = monthlyModeOpt || "day";
    if (mode === "first")
      return firstWeekdayOfMonth(y, m0, monthlyWeekdayOpt ?? 1);
    if (mode === "last")
      return lastWeekdayOfMonth(y, m0, monthlyWeekdayOpt ?? 1);
    return setDayOfMonth(y, m0, monthlyDayOpt ?? d.getDate());
  };

  // Weekly/biweekly with weekday selection (Outlook-style): include selected weekdays
  if (cadence === "weekly") {
    const weeksEvery = Math.max(1, step);
    const w =
      Array.isArray(weekdaysOpt) && weekdaysOpt.length === 7
        ? weekdaysOpt
        : null;

    const anySelected = w ? w.some(Boolean) : false;

    // If none selected, this is invalid (UI will warn). Return no dates.
    if (!anySelected) return [];
    // Scan day-by-day; for each date, include it if:
    // - in an eligible week bucket (weekIndex % weeksEvery === 0)
    // - weekday is selected
    for (let cur = s; cur <= e; cur = addDaysMs(cur, 1)) {
      const dayIndex = Math.floor((cur - s) / 86400000);
      const weekIndex = Math.floor(dayIndex / 7);
      if (weekIndex % weeksEvery !== 0) continue;
      const dow = new Date(cur).getDay();
      const ok = w ? !!w[dow] : false;
      if (!ok) continue;
      push(cur);
    }
  } else {
    // Cadences without weekday selection
    let cur = s;
    // include start date
    if (cadence === "monthly") {
      push(applyMonthlyRule(cur));
    } else {
      push(cur);
    }

    while (cur < e) {
      switch (cadence) {
        case "daily":
          cur = addDaysMs(cur, 1 * step);
          break;
        case "monthly":
          cur = applyMonthlyRule(addMonths(cur, 1 * step));
          break;
        case "quarterly":
          cur = addMonths(cur, 3 * step);
          break;
        default:
          cur = addDaysMs(cur, 7 * step);
          break;
      }
      if (cur > e) break;
      push(cur);
    }
  }

  // ensure end included
  const endIso = isoFromMs(e);
  if (out[out.length - 1] !== endIso) out.push(endIso);
  return out;
}

/**
 * Convenience: given a ReportingSchedule and a date window, generate dates.
 *
 * Unpacks the schedule object into the positional args of buildReportingDates.
 */
export function buildReportingDatesFromSchedule(
  start: string,
  end: string,
  schedule: ReportingSchedule,
): string[] {
  const weekdays = schedule.by_weekday
    ? (Array.isArray(schedule.by_weekday) && schedule.by_weekday.length === 7
        ? schedule.by_weekday.map(Boolean)
        : undefined)
    : undefined;

  return buildReportingDates(
    start,
    end,
    schedule.cadence,
    schedule.interval ?? 1,
    weekdays,
    schedule.monthly_mode,
    schedule.monthly_day_of_month,
    schedule.monthly_weekday,
  );
}

/**
 * Find the most recent reporting date that is on or before `asOfDate`.
 *
 * Returns null if no reporting date falls on or before asOfDate.
 */
export function currentReportingDate(
  reportingDates: string[],
  asOfDate: string,
): string | null {
  if (!reportingDates.length) return null;
  const asOfMs = parseIsoToLocalMs(asOfDate);
  if (!Number.isFinite(asOfMs)) return null;

  let best: string | null = null;
  let bestMs = -Infinity;

  for (const d of reportingDates) {
    const ms = parseIsoToLocalMs(d);
    if (ms <= asOfMs && ms > bestMs) {
      best = d;
      bestMs = ms;
    }
  }
  return best;
}

/**
 * Find the next upcoming reporting date after `asOfDate`.
 *
 * Returns null if no future dates exist in the array.
 */
export function nextReportingDate(
  reportingDates: string[],
  asOfDate: string,
): string | null {
  if (!reportingDates.length) return null;
  const asOfMs = parseIsoToLocalMs(asOfDate);
  if (!Number.isFinite(asOfMs)) return null;

  let best: string | null = null;
  let bestMs = Infinity;

  for (const d of reportingDates) {
    const ms = parseIsoToLocalMs(d);
    if (ms > asOfMs && ms < bestMs) {
      best = d;
      bestMs = ms;
    }
  }
  return best;
}

/**
 * Re-export parseIsoToLocalMs and isoFromMs for consumers that need
 * ad-hoc date arithmetic (e.g. adding grace days to a reporting date).
 */
export { parseIsoToLocalMs, isoFromMs, addDaysMs };
